import { api } from "../../api/api";
import {
  FILE_UPLOAD_WARNING_MESSAGES,
  META_FILE_SIZE_LIMITS,
  NO_CONVERT_IMAGE_FORMATS,
} from "../../constants/constants";

export type IFileExtension =
  | "IMAGE"
  | "PDF"
  | "EXCEL"
  | "VIDEO"
  | "CSV"
  | "OTHER"
  | "AUDIO";

export const audioExtensions = [
  "mp3",
  "wav",
  "aac",
  "m4a",
  "ogg",
  "oga",
  "opus",
  "flac",
  "alac",
  "wma",
  "aiff",
  "aif",
  "aifc",
  "amr",
  "caf",
];

export const imageExtensions = [
  "jpg",
  "jpeg",
  "jpe",
  "jfif",
  "png",
  "gif",
  "bmp",
  "tif",
  "tiff",
  "webp",
  "heif",
  "heic",
  "avif",
];

export const videoExtensions = [
  "mp4",
  "m4v",
  "mov",
  "avi",
  "mkv",
  "webm",
  "wmv",
  "flv",
  "f4v",
  "3gp",
  "3g2",
  "ts",
  "mts",
  "m2ts",
];

const convertToBase64 = (file: any) => {
  return new Promise<string>((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
      resolve(fileReader.result as string);
    };
    fileReader.onerror = (error) => {
      reject(error);
    };
  });
};

export const convertBase64ToBlob = (base64Image: string) => {
  const parts = base64Image.split(";base64,");
  const imageType = parts[0].split(":")[1];
  const decodedData = window.atob(parts[1]);
  const uInt8Array = new Uint8Array(decodedData.length);
  for (let i = 0; i < decodedData.length; ++i) {
    uInt8Array[i] = decodedData.charCodeAt(i);
  }
  return new Blob([uInt8Array], { type: imageType });
};

export const isImageFile = (fileName: string): boolean => {
  const extension = fileName.split(".").pop();
  if (imageExtensions.includes(extension || "")) return true;
  return false;
};

export const isPdfFile = (fileName: string): boolean => {
  const extension = fileName.split(".").pop();
  if (extension === "pdf" || extension === "PDF") return true;
  return false;
};

export const isExcelFile = (fileName: string): boolean => {
  const extension = fileName.split(".").pop();
  if (
    extension === "xls" ||
    extension === "xlsx" ||
    extension === "XLS" ||
    extension === "XLSX"
  )
    return true;
  return false;
};

export const getFileExtensionType = (fileName: string): IFileExtension => {
  if (!fileName || typeof fileName !== "string") {
    return "OTHER";
  }
  const extension = fileName?.split(".").pop()?.toLocaleLowerCase();

  if (imageExtensions.includes(extension || "")) return "IMAGE";
  if (videoExtensions.includes(extension || "")) return "VIDEO";
  if (audioExtensions.includes(extension || "")) return "AUDIO";
  if (extension === "pdf") return "PDF";
  if (extension === "xls" || extension === "xlsx") return "EXCEL";
  if (extension === "csv") return "CSV";

  return "OTHER";
};

export const getFileExtension = (fileName: string): string => {
  return fileName.split(".").pop()?.toLocaleLowerCase() || "";
};

export const getBase64Extension = (extension: string): string => {
  const fileType = getFileExtensionType(extension);
  if (fileType === "IMAGE") return `data:image/${extension};base64`;
  if (fileType === "VIDEO") return `data:video/${extension};base64`;
  if (fileType === "EXCEL")
    return `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64`;
  return `data:application/${extension};base64`;
};

const arrayBufferToBase64 = (buffer: any, extension: string): string => {
  return `${getBase64Extension(extension)},${btoa(
    String.fromCharCode.apply(null, buffer)
  )}`;
};

const arrayBufferToBase64V2 = (buffer: any, extension: string): string => {
  return `${getBase64Extension(extension)},${btoa(
    new Uint8Array(buffer).reduce(function (data, byte) {
      return data + String.fromCharCode(byte);
    }, "")
  )}`;
};

export const downloadSingleFileAsync = async (fileUrl: string) =>
  new Promise<{ fileName: string; data: string; type: IFileExtension }>(
    (resolve, reject) => {
      const payload = {
        type: "",
        keys: [fileUrl],
      };
      api
        .post("/general/download-files", payload)
        .then((res) => {
          const fileName = fileUrl?.split("/")[fileUrl?.split("/").length - 1];
          const fileExtension = fileName.split(".")[1];
          const base64Data = arrayBufferToBase64V2(
            res.data[0]?.data,
            fileExtension
          );
          resolve({
            fileName: fileName,
            data: base64Data,
            type: getFileExtensionType(fileUrl),
          });
        })
        .catch((err) => {
          reject(err);
        });
    }
  );

export const convertBase64ToUrl = (
  base64: string,
  type = "application/octet-stream"
) => {
  const binStr = atob(base64);
  const len = binStr.length;
  const arr = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    arr[i] = binStr.charCodeAt(i);
  }
  const blob = new Blob([arr], { type: type });
  return URL.createObjectURL(blob);
};

export const getExcelFileSignedUrlAsync = async (path: string) =>
  new Promise<{ fileName: string; data: string; type: IFileExtension }>(
    (resolve, reject) => {
      api
        .get(`/general/get-signed-url?key=${path}`)
        .then((res) => {
          const fileName = path.split("/")[path.split("/").length - 1];

          resolve({
            fileName: fileName,
            data: res.data.data,
            type: getFileExtensionType(path),
          });
        })
        .catch((err) => {
          reject(err);
        });
    }
  );

export const shouldConvertFile = (file: File): boolean => {
  const mimeType = file.type.toLowerCase();
  const fileType = getFileExtensionType(file.name);
  const fileExtension = getFileExtension(file.name);

  // Only apply conversion logic for image files
  if (fileType !== "IMAGE") {
    return false;
  }

  // For images: PNG and JPEG don't need conversion, other image formats do
  return !NO_CONVERT_IMAGE_FORMATS.includes(fileExtension);
};

export const shouldCompressFile = (file: File): boolean => {
  const fileType = getFileExtensionType(file.name);
  const fileSize = file.size;

  switch (fileType) {
    case "IMAGE":
      return fileSize > META_FILE_SIZE_LIMITS.IMAGE;
    case "VIDEO":
      return fileSize > META_FILE_SIZE_LIMITS.VIDEO;
    case "PDF":
    case "EXCEL":
    case "OTHER":
    default:
      // For documents and other file types
      return fileSize > META_FILE_SIZE_LIMITS.DOCUMENT;
  }
};

export const getFileUploadWarningMessage = (
  isConvert: boolean,
  isCompress: boolean
): string | null => {
  if (isConvert && isCompress) {
    return FILE_UPLOAD_WARNING_MESSAGES.CONVERT_AND_COMPRESS;
  }
  if (isConvert) {
    return FILE_UPLOAD_WARNING_MESSAGES.CONVERT_ONLY;
  }
  if (isCompress) {
    return FILE_UPLOAD_WARNING_MESSAGES.COMPRESS_ONLY;
  }
  return null;
};

export const getContentType = (file: File): string => {
  const fileType = getFileExtensionType(file.name);

  switch (fileType) {
    case "IMAGE":
      return file.name.split(".").pop() === "png" ||
        file.name.split(".").pop() === "PNG"
        ? `image/${file.name.split(".").pop()}`
        : `image/jpeg`;

    case "VIDEO":
      return `video/${file.name.split(".").pop()}`;

    case "EXCEL":
      return `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`;

    case "CSV":
      return `text/csv`;

    default:
      return `application/pdf`;
  }
};
